@if (busy) {
  <div class="flex flex-col items-center justify-center py-8">
    <div class="text-center">
        <div class="relative w-20 h-20 mx-auto mb-6">
          <div class="absolute inset-0 rounded-full border-4 border-green-200 animate-spin"></div>
          <div class="absolute inset-2 rounded-full border-4 border-green-400 border-t-transparent animate-spin" style="animation-direction: reverse; animation-duration: 1.5s;"></div>
          <div class="absolute inset-0 flex items-center justify-center">
            <mat-icon class="text-green-600 text-2xl animate-pulse">security</mat-icon>
          </div>
        </div>
        <h3 class="mb-2 text-gray-700 inline-flex">
          <mat-icon class="mr-2">account_balance</mat-icon>
          Secure Bank Connection Active
        </h3>
        <p class="text-gray-500">Please wait...</p>
      </div>
  </div>
}

<div [class.hidden]="busy">
  <div class="p-2 sm:p-4">
    <form [formGroup]="bankAccountForm" (submit)="onSubmit()">
      
      @if (isEditMode) {
        <div class="flex flex-col gap-6">
          <!-- Bank Account Preview Card -->
          <div class="overflow-hidden border border-blue-400 bg-blue-100 rounded-xl p-6">
            <div class="flex items-center gap-2 mb-4">
              <mat-icon class="text-gray-700">account_balance</mat-icon>
              <h3>{{ userBankAccount?.bankName }}</h3>
            </div>
            
            <div class="grid grid-cols-2">
              <div>
                <p class="uppercase text-sm font-medium">Account Type</p>
                <p class="font-medium">{{ bankAccountType[userBankAccount?.accountType] | titlecase }}</p>
              </div>
              <div>
                <p class="uppercase text-sm font-medium">Account Number</p>
                <p class="font-medium tracking-wider">••••{{ userBankAccount?.last4Digits }}</p>
              </div>
            </div>
          </div>

          <!-- Edit Section -->
          <div>
            <div class="w-full mb-2 flex flex-col">
              <mat-checkbox formControlName="updateJobs">Assign To All Jobs</mat-checkbox>
              @if (sideMarkOptions?.length > 1) {
                <mat-checkbox formControlName="updateProjects">Assign To Sidemark</mat-checkbox>
              }
            </div>

            @if (showSideMark) {
              <mat-form-field class="w-full mb-2" [subscriptSizing]="'dynamic'">
                <mat-label>Sidemark</mat-label>
                <mat-select formControlName="projectIds" multiple>
                  @for (item of sideMarkOptions; track item?.value) {
                    <mat-option [disabled]="isDisableSideMarkOption(item.value.toString())" [value]="item.value">{{ item.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }

            <mat-form-field class="w-full edit-mode-field" [subscriptSizing]="'dynamic'">
              <mat-label>Nickname</mat-label>
              <input matInput type="text" formControlName="nickname" 
                    placeholder="Enter a friendly name for this account">
              <mat-hint>This nickname helps you identify this account easily</mat-hint>
            </mat-form-field>
             
            <div class="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div class="flex gap-1">
                <mat-icon class="text-amber-600 text-base">info</mat-icon>
                <p class="text-sm text-amber-800">
                  <strong>Note:</strong> Only the nickname and sidemark assignments can be modified. 
                  For other changes, please add a new account or contact support.
                </p>
              </div>
            </div>
           </div>
        </div>
      } @else {
        <div class="w-full pb-4">
          <div class="flex flex-col w-full gap-3">
            <!-- Ownership and Account Type -->
            <div class="grid grid-cols-2 gap-x-2">
              <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                <mat-label>Ownership Type</mat-label>
                <mat-select formControlName="ownershipType">
                  @for (type of options.ownershipType; track type?.value) {
                    <mat-option [value]="type.value">{{ type.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                <mat-label>Account Type</mat-label>
                <mat-select formControlName="accountType">
                  @for (type of options.accountType; track type?.value) {
                    <mat-option [value]="type.value">{{ type.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>

            <!-- Personal Account Fields -->
            @if (bankAccountControls.ownershipType.value === bankAccountOwnershipType.Personal) {
              <div class="grid grid-cols-2 gap-x-2 items-start w-full">
                <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                  <mat-label>First Name</mat-label>
                  <input matInput type="text" formControlName="firstName">
                  @if (bankAccountControls.firstName.invalid && bankAccountControls.firstName.touched) {
                    <mat-error>
                      @if (bankAccountControls.firstName.errors?.['required']) {
                        First name is required
                      }
                    </mat-error>
                  }
                </mat-form-field>
                <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                  <mat-label>Last Name</mat-label>
                  <input matInput type="text" formControlName="lastName">
                  @if (bankAccountControls.lastName.invalid && bankAccountControls.lastName.touched) {
                    <mat-error>
                      @if (bankAccountControls.lastName.errors?.['required']) {
                        Last name is required
                      }
                    </mat-error>
                  }
                </mat-form-field>
              </div>
            }

            <!-- Business Account Fields -->
            @if (bankAccountControls.ownershipType?.value === bankAccountOwnershipType.Business) {
              <div>
                <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                  <mat-label>Business Name</mat-label>
                  <input matInput type="text" formControlName="businessName">
                  @if (bankAccountControls.businessName?.invalid && bankAccountControls.businessName?.touched) {
                    <mat-error>
                      @if (bankAccountControls.businessName?.errors?.['required']) {
                        Business name is required
                      }
                    </mat-error>
                  }
                </mat-form-field>
              </div>
            }

            <!-- Account Holder Name -->
            <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
              <mat-label>Account Holder Name</mat-label>
              <input matInput type="text" formControlName="accountHolderName" required>
              @if (bankAccountControls.accountHolderName.invalid && bankAccountControls.accountHolderName.touched) {
                <mat-error>
                  @if (bankAccountControls.accountHolderName.errors?.['required']) {
                    Account holder name is required
                  }
                </mat-error>
              }
            </mat-form-field>

            <!-- Routing and Account Numbers -->
            <div class="grid grid-cols-2 gap-x-2 items-start w-full">
              <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                <mat-label>Routing Number</mat-label>
                <input matInput type="text" formControlName="routingNumber" 
                       mask="000000000" 
                       placeholder="Enter routing number">
                @if (bankAccountControls.routingNumber.invalid && bankAccountControls.routingNumber.touched) {
                  <mat-error>
                    @if (bankAccountControls.routingNumber.errors?.['required']) {
                      Routing number is required
                    }
                    @if (bankAccountControls.routingNumber.errors?.['minlength']) {
                      Routing number must be 9 digits
                    }
                  </mat-error>
                }
              </mat-form-field>
              <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                <mat-label>Account Number</mat-label>
                <input matInput type="text" formControlName="accountNumber" 
                  mask="00000000000000000"
                  [validation]="!(bankAccountForm?.controls?.accountNumber?.value?.length <= 17)" 
                  [dropSpecialCharacters]="false"
                  placeholder="Enter account number">
                @if (bankAccountControls.accountNumber.invalid && bankAccountControls.accountNumber.touched) {
                  <mat-error>
                    @if (bankAccountControls.accountNumber.errors?.['required']) {
                      Account number is required
                    }
                    @if (bankAccountControls.accountNumber.errors?.['minlength']) {
                      Account number must be at least 4 digits
                    }
                    @if (bankAccountControls.accountNumber.errors?.['maxlength']) {
                      Account number cannot exceed 17 digits
                    }
                  </mat-error>
                }
              </mat-form-field>
            </div>

            <!-- Billing Address -->
            <div class="flex flex-col gap-3 items-start">
              <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                <mat-label>Street Address</mat-label>
                <input matInput type="text" formControlName="billingStreetAddress">
                @if (bankAccountControls.billingStreetAddress?.invalid && bankAccountControls.billingStreetAddress?.touched) {
                  <mat-error>
                    @if (bankAccountControls.billingStreetAddress?.errors?.['required']) {
                      Street address is required
                    }
                  </mat-error>
                }
              </mat-form-field>
              
              <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                <mat-label>Address Line 2 (Optional)</mat-label>
                <input matInput type="text" formControlName="billingExtendedAddress" placeholder="Apartment, suite, unit, building, floor, etc.">
              </mat-form-field>
              
              <div class="grid grid-cols-3 gap-x-2 items-start w-full">
                <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                  <mat-label>City</mat-label>
                  <input matInput type="text" formControlName="billingLocality">
                  @if (bankAccountControls.billingLocality?.invalid && bankAccountControls.billingLocality?.touched) {
                    <mat-error>
                      @if (bankAccountControls.billingLocality?.errors?.['required']) {
                        City is required
                      }
                    </mat-error>
                  }
                </mat-form-field>
                <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                  <mat-label>State</mat-label>
                  <mat-select formControlName="billingRegion">
                    @for (state of options.states; track state?.value) {
                      <mat-option [value]="state.value">{{ state.label }}</mat-option>
                    }
                  </mat-select>
                  @if (bankAccountControls.billingRegion?.invalid && bankAccountControls.billingRegion?.touched) {
                    <mat-error>
                      @if (bankAccountControls.billingRegion?.errors?.['required']) {
                        State is required
                      }
                    </mat-error>
                  }
                </mat-form-field>
                <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                  <mat-label>Postal Code</mat-label>
                  <input matInput type="text" formControlName="billingPostalCode" 
                         mask="00000||00000-0000" 
                         placeholder="12345 or 12345-6789">
                  @if (bankAccountControls.billingPostalCode?.invalid && bankAccountControls.billingPostalCode?.touched) {
                    <mat-error>
                      @if (bankAccountControls.billingPostalCode?.errors?.['required']) {
                        Postal Code is required
                      }
                    </mat-error>
                  }
                </mat-form-field>
              </div>
            </div>
          </div>
        </div>

        <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
          <mat-label>Nickname</mat-label>
          <input matInput type="text" formControlName="nickname" id="nickname">
        </mat-form-field>

        <mat-checkbox formControlName="setAsDefault" class="py-2">Set as Default Payment Method</mat-checkbox>

        <div class="w-full flex flex-col gap-2 pb-2">
          <mat-checkbox formControlName="updateJobs">Assign To All Jobs</mat-checkbox>
          @if (sideMarkOptions?.length > 1) {
            <mat-checkbox formControlName="updateProjects">Assign To Sidemark</mat-checkbox>
          }
        </div>

        @if (showSideMark) {
          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
            <mat-label>Sidemark</mat-label>
            <mat-select formControlName="projectIds" multiple>
              @for (item of sideMarkOptions; track item?.value) {
                <mat-option [disabled]="isDisableSideMarkOption(item.value.toString())" [value]="item.value">{{ item.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }


        <div class="w-full p-4 bg-gray-50 rounded-lg flex gap-2">
          <mat-icon class="icon-size-5 mt-1">info</mat-icon>
          <p class="text-gray-600">By adding this bank account, I authorize Metropolitan to debit my bank account for agreed charges and terms.</p>
        </div>
      }

      <!-- Form Actions -->
      <div class="flex flex-row mt-4 justify-between">
        <button type="button" class="bg-white" mat-raised-button color="default" (click)="onCancel()">
          <mat-icon>arrow_back</mat-icon>
          Cancel
        </button>

        <button type="submit"
          [disabled]="!bankAccountForm.valid || (bankAccountForm.pristine && !isEditMode)"
          id="submit-bank-account-form" mat-raised-button color="primary">
          {{ isEditMode ? 'Update' : 'Save' }} 
          <mat-icon svgIcon="heroicons_outline:check-circle"></mat-icon>
        </button>
      </div>

    </form>
  </div>
</div>